// Тесният списък вляво от панела на „Продадени" (≥ 1024 px) — огледало на
// `stockCard/StockCardMasterList`: докато панелът е отворен, той заема мястото
// на таблицата. Продукти: „Продукт · Разлика" (⇄ при замяна); рецепти:
// „Рецепта · Продадено". Изборът СЛЕДВА фокуса — ↑/↓ (и Home/End) сменят
// реда в панела, Enter/→ влиза в панела, Esc го затваря (глобално,
// `useSoldSelection`). Редовете са същите като в таблицата (филтрирани,
// сортирани, до „Покажи още").
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { KbdChip } from "../../components/KbdChip";
import { ROW_READ } from "../../components/list/density";
import { REPORT_HEAD_CLASS, REPORT_HEADER_ROW_CLASS } from "../components/reportListModel";
import { formatCount } from "../components/soldReportModel";
import { SignedQuantity, SubstitutionMark } from "./SoldVarianceBits";
import type { SoldRow } from "./soldVarianceModel";

type Props = {
  rows: SoldRow[];
  isProduct: boolean;
  selectedId: number;
  decimalsOf: (productId: number) => number;
  onSelect: (row: SoldRow) => void;
  onEnterPanel: () => void;
  footer?: ReactNode;
};

export const SoldMasterList = ({ rows, isProduct, selectedId, decimalsOf, onSelect, onEnterPanel, footer }: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);

  const optionOf = (sellableId: number) =>
    listRef.current?.querySelector<HTMLElement>(`[data-sold-option="${sellableId}"]`) ?? null;

  // При отваряне фокусът идва тук (от реда на таблицата, която се скрива) —
  // иначе ↑/↓ веднага след Enter нямаше да сменят реда.
  useEffect(() => {
    optionOf(selectedId)?.focus({ preventScroll: false });
    // само при монтиране: после фокусът се мести от самите клавиши
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (index: number) => {
    const target = rows[index];
    if (!target) return;
    onSelect(target);
    const option = optionOf(target.sellableId);
    option?.focus();
    option?.scrollIntoView?.({ block: "nearest" });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = rows.findIndex((row) => row.sellableId === selectedId);
    let next: number | null = null;
    if (event.key === "ArrowDown") next = Math.min(index + 1, rows.length - 1);
    else if (event.key === "ArrowUp") next = Math.max(index - 1, 0);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = rows.length - 1;
    else if (event.key === "Enter" || event.key === "ArrowRight") {
      event.preventDefault();
      onEnterPanel();
      return;
    }
    if (next == null) return;
    event.preventDefault();
    move(next);
  };

  return (
    <div className="@container flex flex-col">
      <div className={`flex items-center gap-3 px-3.5 ${REPORT_HEADER_ROW_CLASS}`}>
        <span className={`${REPORT_HEAD_CLASS} flex-1`}>
          {t(isProduct ? "inventoryPage.colProductName" : "inventoryPage.recipe")}
        </span>
        <span className="hidden items-center gap-1 text-[11.5px] leading-[normal] text-muted @min-[440px]:inline-flex">
          <KbdChip>↑</KbdChip>
          <KbdChip>↓</KbdChip>
          {t("inventoryPage.sdKeysSwitch")}
          <KbdChip>Esc</KbdChip>
          {t("inventoryPage.scKeysClose")}
        </span>
        <span className={REPORT_HEAD_CLASS}>
          {t(isProduct ? "inventoryPage.sdGroupDiff" : "inventoryPage.soldColSold")}
        </span>
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-label={t(isProduct ? "inventoryPage.soldProducts" : "inventoryPage.soldRecipes")}
        onKeyDown={onKeyDown}
      >
        {rows.map((row) => {
          const active = row.sellableId === selectedId;
          const decimals = decimalsOf(row.sellableId);
          return (
            <div
              key={row.sellableId}
              role="option"
              aria-selected={active}
              data-sold-option={row.sellableId}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect(row)}
              className={`flex cursor-pointer items-center gap-2.5 border-l-[3px] border-t border-t-shade-1 pl-[11px] pr-3.5 ${ROW_READ} outline-none focus-visible:bg-fill ${
                active ? "border-l-brand bg-brand-tint" : "border-l-transparent hover:bg-fill"
              }`}
            >
              <span
                className={`min-w-0 flex-1 truncate text-[13.5px] leading-[1.3] ${
                  active ? "font-semibold text-ink" : "text-ink-2"
                }`}
              >
                {row.name}
              </span>
              {isProduct && <SubstitutionMark actual={row.actual} decimals={decimals} unit={row.unit} />}
              <span className="shrink-0 whitespace-nowrap text-[13px]">
                {isProduct ? (
                  <SignedQuantity value={row.variance?.quantity ?? null} decimals={decimals} unit={row.unit} />
                ) : (
                  <span className="font-sheet tabular-nums text-ink-2">{formatCount(row.quantity)}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
};

export default SoldMasterList;
